import React, { useState } from "react";
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Text,
  Input,
  ModalCloseButton,
  Select,
  ModalFooter,
  Textarea,
  Checkbox,
  FormErrorMessage,
} from "@chakra-ui/react";
import axios from "axios";
import { API_URL } from "../config";
import { getToken } from "../token";
import { findCategory } from "../categories";
import { FORUM } from "../Theme/styles";
import AssigneePicker from "./AssigneePicker";
import ImagePicker from "./ImagePicker";
import ItemList from "./ItemList";

const AddTaskModal = ({ isOpen, onClose, category, peoples }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assignments, setPrzydzialy] = useState([]);
  const [piority, setPiority] = useState("");
  const [images, setImages] = useState([]);
  const [withDeadline, setZTerminem] = useState(false);
  const [deadline, setTermin] = useState("");
  const [sending, setWysylam] = useState(false);
  const [submitted, setDotknietoWysylki] = useState(false);

  const categoryName = findCategory(category)?.name || category;
  const noTitle = !title.trim();
  const noPriority = !piority;
  const noAssignee = assignments.length === 0;
  const noDeadline = withDeadline && !deadline;

  const reset = () => {
    setTitle("");
    setDescription("");
    setPrzydzialy([]);
    setPiority("");
    setImages([]);
    setZTerminem(false);
    setTermin("");
    setDotknietoWysylki(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleTaskAdd = async () => {
    setDotknietoWysylki(true);
    if (noTitle || noPriority || noAssignee || noDeadline || sending) return;

    const opisZeZdjeciami = [description.trim(), ...images]
      .filter(Boolean)
      .join("\n");

    setWysylam(true);
    try {
      await axios.post(
        `${API_URL}/tasks/add`,
        {
          title: title.trim(),
          description: opisZeZdjeciami,
          piority: piority,
          deadline: withDeadline && deadline ? deadline : null,
          assignments: assignments.map((p) => ({
            kind: p.kind,
            team: p.team,
            assigned_to: p.assigned_to || null,
            deadline: p.deadline || null,
            items: (p.items || [])
              .filter((x) => x.description.trim())
              .map((x) => ({ description: x.description.trim() })),
          })),
        },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      close();
    } catch (err) {
      console.error("Nie udało się dodać zadania", err);
    } finally {
      setWysylam(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={close} size={{ base: "full", md: "lg" }} scrollBehavior={"inside"}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex direction={"column"} gap={0.5}>
            <Text
              fontSize={"11px"}
              fontWeight={"500"}
              letterSpacing={"0.08em"}
              textTransform={"uppercase"}
              color={FORUM.tekstPrzygaszony}
            >
              {categoryName}
            </Text>
            <Text fontSize={"lg"} fontWeight={"600"} color={FORUM.tekstJasny}>
              Nowe zadanie
            </Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton mt={2} />

        <ModalBody>
          <Flex direction={"column"} gap={5}>
            <FormControl isRequired isInvalid={submitted && noTitle}>
              <FormLabel>Tytuł</FormLabel>
              <Input
                placeholder="Krótko: czego dotyczy zadanie"
                value={title}
                autoFocus
                onChange={(e) => setTitle(e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel>
                Opis{" "}
                <Text as={"span"} fontSize={"xs"} color={FORUM.tekstPrzygaszony}>
                  — opcjonalny
                </Text>
              </FormLabel>
              <Textarea
                placeholder="Co dokładnie trzeba zrobić, gdzie to występuje, jak powtórzyć"
                value={description}
                rows={5}
                resize={"vertical"}
                onChange={(e) => setDescription(e.target.value)}
              />
            </FormControl>

            <Flex gap={5} direction={{ base: "column", sm: "row" }}>
              <FormControl
                isRequired
                isInvalid={submitted && noPriority}
                flex={1}
              >
                <FormLabel>Priorytet</FormLabel>
                <Select
                  placeholder="Wybierz"
                  value={piority}
                  onChange={(e) => setPiority(e.target.value)}
                >
                  <option value="niski">Niski</option>
                  <option value="sredni">Średni</option>
                  <option value="wysoki">Wysoki</option>
                </Select>
              </FormControl>

              <FormControl isInvalid={submitted && noDeadline} flex={1}>
                <FormLabel mb={1.5}>
                  <Checkbox
                    isChecked={withDeadline}
                    onChange={(e) => {
                      setZTerminem(e.target.checked);
                      if (!e.target.checked) setTermin("");
                    }}
                    size={"sm"}
                  >
                    <Text fontSize={"sm"} color={FORUM.tekstPrzygaszony}>
                      Termin
                    </Text>
                  </Checkbox>
                </FormLabel>
                <Input
                  type={"date"}
                  value={deadline}
                  isDisabled={!withDeadline}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setTermin(e.target.value)}
                />
                <FormErrorMessage fontSize={"xs"}>Podaj datę.</FormErrorMessage>
              </FormControl>
            </Flex>

            <FormControl isRequired isInvalid={submitted && noAssignee}>
              <FormLabel>Przypisz do</FormLabel>
              <FormErrorMessage fontSize={"xs"} mt={0} mb={2}>
                Wskaż przynajmniej jedną osobę albo zespół.
              </FormErrorMessage>
              <AssigneePicker
                people={peoples}
                assignments={assignments}
                onChange={setPrzydzialy}
                categoryName={categoryName}
                categoryOfBoard={category}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Zdjęcia</FormLabel>
              <ImagePicker images={images} onChange={setImages} />
            </FormControl>

            {assignments.length > 0 && (
              <FormControl>
                <FormLabel>
                  Co ma zrobić każdy z nich{" "}
                  <Text as={"span"} fontSize={"xs"} color={FORUM.tekstPrzygaszony}>
                    — opcjonalne
                  </Text>
                </FormLabel>
                {assignments.map((p, indeks) => (
                  <ItemList
                    key={`${p.kind}-${p.team}-${p.assigned_to || ""}`}
                    assignment={p}
                    people={peoples}
                    highlight={false}
                    onChange={(items) =>
                      setPrzydzialy(
                        assignments.map((x, i) => (i === indeks ? { ...x, items } : x))
                      )
                    }
                    onDeadlineChange={(deadline) =>
                      setPrzydzialy(
                        assignments.map((x, i) => (i === indeks ? { ...x, deadline } : x))
                      )
                    }
                    onTeamChange={(team) =>
                      setPrzydzialy(
                        assignments.map((x, i) => (i === indeks ? { ...x, team } : x))
                      )
                    }
                  />
                ))}
              </FormControl>
            )}

          </Flex>
        </ModalBody>

        <ModalFooter gap={2}>
          <Button variant={"drugorzedny"} onClick={close}>
            Anuluj
          </Button>
          <Button
            variant={"glowny"}
            onClick={handleTaskAdd}
            isLoading={sending}
            loadingText={"Dodaję"}
          >
            Dodaj zadanie
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddTaskModal;
